import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

export const LoadingOverlay = () => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('sargo_loader_seen') !== '1';
  });
  const [shouldRender, setShouldRender] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('sargo_loader_seen') !== '1';
  });
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!shouldRender) return;

    setIsVisible(true);
    document.body.style.overflow = 'hidden';

    // Progress animation
    const duration = 2500; // Slightly faster total duration
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const rawProgress = Math.min((elapsed / duration) * 100, 100);
      
      // Pseudo-progress: fast then slow then hit 100
      let easedProgress;
      if (rawProgress < 70) {
        easedProgress = rawProgress * 1.1; // Faster at start
      } else if (rawProgress < 90) {
        easedProgress = 77 + (rawProgress - 70) * 0.6; // Slower
      } else {
        easedProgress = 89 + (rawProgress - 90) * 1.1; // Final push
      }
      
      const finalProgress = Math.min(easedProgress, 100);
      setProgress(finalProgress);

      if (finalProgress < 100) {
        requestAnimationFrame(updateProgress);
      } else {
        // Exit sequence
        setTimeout(() => {
          setIsVisible(false);
          sessionStorage.setItem('sargo_loader_seen', '1');
          document.body.style.overflow = '';
        }, 600); // Give user time to see 100%
      }
    };

    requestAnimationFrame(updateProgress);
  }, [prefersReducedMotion, shouldRender]);

  if (!shouldRender) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#13100B]"
        >
          <div className="relative flex flex-col items-center">
            {/* Logo Block */}
            <div className="flex flex-col items-center mb-10">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/blink-451505.firebasestorage.app/o/user-uploads%2FrwS8pE6ETXeTYYLtNc6pZgfMLz22%2F12__5592f8cf.png?alt=media&token=1a2fec56-b439-449d-bc26-9a1e1f45b75e" 
                alt="SARGO" 
                className="h-14 md:h-18 w-auto object-contain mb-0"
              />
              <span className="text-white/40 text-[10px] tracking-[0.4em] font-medium uppercase leading-none mt-[-2px]">
                SPC · MANUFACTURER
              </span>
            </div>

            {/* Loading Block */}
            <div className="flex flex-col items-center">
              <span className="text-white text-[12px] tracking-[0.2em] font-bold uppercase mb-6">
                LOADING... {Math.round(progress)}%
              </span>

              {/* Progress Bar Container */}
              <div className="relative w-64 h-[2px] bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-[#B08A5A]"
                  initial={{ width: "0%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.1 }}
                />
              </div>
            </div>
          </div>

          {/* Vignette Overlay */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/10 to-transparent" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};